import { Box, Button, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import SectionHeader from './SectionHeader'
import { activityMeta } from './activityMeta'
import type { ActivityEvent } from './dashboardTypes'

function useRelativeTime() {
  const { i18n } = useTranslation()
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' })
  return (minutesAgo: number) => {
    if (minutesAgo < 60) return rtf.format(-minutesAgo, 'minute')
    if (minutesAgo < 60 * 24) return rtf.format(-Math.round(minutesAgo / 60), 'hour')
    return rtf.format(-Math.round(minutesAgo / (60 * 24)), 'day')
  }
}

type RecentActivityCardProps = {
  events: ActivityEvent[]
  /** Navigate to the full Activity tab. */
  onShowAll?: () => void
}

export default function RecentActivityCard({ events, onShowAll }: RecentActivityCardProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const formatRelative = useRelativeTime()

  const meta = activityMeta(c)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      }}
    >
      <SectionHeader
        title={t('recentActivity.title')}
        subtitle={t('recentActivity.subtitle')}
        right={
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: 11,
                fontWeight: 600,
                color: c.success,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: c.success,
                  animation: 'affexy-pulse 1.6s ease-in-out infinite',
                }}
              />
              {t('recentActivity.live')}
            </Box>
            {onShowAll && (
              <Button color="inherit" sx={{ color: c.ink72, fontSize: 13 }} onClick={onShowAll}>
                {t('recentActivity.showAll')}
              </Button>
            )}
          </Box>
        }
      />

      <Box>
        {events.length === 0 && (
          <Typography sx={{ fontSize: 13, color: c.ink56, py: 1 }}>{t('recentActivity.empty')}</Typography>
        )}
        {events.map((e, i) => {
          const { Icon, color } = meta[e.verbKey]
          return (
            <Box
              key={e.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                py: 1.25,
                borderBottom: i < events.length - 1 ? `1px dashed ${c.ink08}` : 'none',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: `${theme.affexy.radii.sm + 1}px`,
                  bgcolor: c.gray50,
                  display: 'grid',
                  placeItems: 'center',
                  flex: '0 0 28px',
                  color,
                }}
              >
                <Icon sx={{ fontSize: 15 }} />
              </Box>
              <Typography sx={{ flex: 1, fontSize: 13, lineHeight: 1.4, minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {e.who}
                </Box>{' '}
                <Box component="span" sx={{ color: c.ink72 }}>
                  {t(`recentActivity.verb.${e.verbKey}`, { count: e.count ?? 0 })}
                </Box>
                {e.target && (
                  <>
                    {' '}
                    <Box component="span" sx={{ color: c.coral, fontWeight: 500 }}>
                      {e.target}
                    </Box>
                  </>
                )}
              </Typography>
              <Typography
                sx={{ fontSize: 11.5, color: c.ink48, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {formatRelative(e.minutesAgo)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}
